import React, { createContext, useMemo, useContext } from 'react';
import type { TranslateFunc } from 'src/components/translate';
import { translator } from 'src/components/translate';
import type { ClientMarket } from '@prisjakt/web-app-config/types/client-market';
import seedrandom from 'seedrandom';
import type { sentryPromise as sentryPromiseForTypeof } from '../../client/sentry';

import { linkTo } from './link-to';
import type { ContextsQuery } from '../../types';

export { linkTo } from './link-to';

declare global {
  interface Window {
    RNG_SEED: string;
  }
}

type GetLinkToArguments<F> = F extends (
  useClassicUrls: any,
  useHashForProductTabs: any,
  classicRoutes: any,
  routes: any,
  fallbackRedirects: any,
  ...rest: infer R
) => any
  ? R
  : never;
type LinkToArguments = GetLinkToArguments<typeof linkTo>;

export type LinkToFunc = (
  ...args: LinkToArguments
) => ReturnType<typeof linkTo>;

export type AppContextValue = {
  renderMode: RENDER_MODES;
  market: ClientMarket;
  linkTo: LinkToFunc;
  t: TranslateFunc;
  captureMessage: (data: any) => void;
  captureException: (data: any) => void;
  random: seedrandom.PRNG;
  sentry: typeof sentryPromiseForTypeof;
  productOfTheYear: ContextsQuery['productsOfTheYear'];
};

const crash = () => {
  throw new Error('AppContext value is not initialized.');
};

const initialValue: any = {
  renderMode: '',
  market: '',
  linkTo: crash,
  t: crash,
  Translate: crash,
  captureMessage: crash,
  captureException: crash,
  productOfTheYear: [],
};

export const AppContext = createContext<AppContextValue>(initialValue);
AppContext.displayName = 'AppContext';

const captureEvent = (
  sentryPromise: typeof sentryPromiseForTypeof,
  sentryMethodName: 'captureMessage' | 'captureException',
  data: any
) => {
  sentryPromise
    .then(sentry => {
      sentry[sentryMethodName](data);
    })
    .catch(() => {
      const consoleMethodName = {
        captureMessage: 'log',
        captureException: 'error',
      }[sentryMethodName];
      console[consoleMethodName](data); // eslint-disable-line no-console
    });
};

type Translations = {
  [domain: string]: {
    [text: string]: string;
  };
};

export const AppContextContainer = ({
  children,
  translations,
  renderMode,
  market,
  locale,
  sentryPromise,
  rngSeed,
  productOfTheYear = [],
}: {
  children: React.ReactNode;
  translations: Translations;
  market: NonNullable<AppContextValue>['market'];
  locale: string;
  sentryPromise: typeof sentryPromiseForTypeof;
  renderMode: NonNullable<AppContextValue>['renderMode'];
  rngSeed: string;
  productOfTheYear: AppContextValue['productOfTheYear'];
}) => {
  const context = useMemo(() => {
    const translate = translator(translations, locale);
    const ctx: AppContextValue = {
      renderMode,
      market,
      random: seedrandom(rngSeed),
      linkTo: (...args) =>
        // .bind only has TS support with up to 4 arguments, so using wrapper function here instead.
        linkTo(
          market.useClassicUrls,
          market.useHashForProductTabs,
          market.classicRoutes,
          market.routes,
          market.fallbackRedirects,
          ...args
        ),
      t: translate,
      captureMessage: (data: string) =>
        captureEvent(sentryPromise, 'captureMessage', data),
      captureException: (data: Error) => {
        captureEvent(sentryPromise, 'captureException', data);
      },
      sentry: sentryPromise,
      productOfTheYear,
    };

    return ctx;
  }, [
    translations,
    locale,
    renderMode,
    market,
    sentryPromise,
    rngSeed,
    productOfTheYear,
  ]);

  return <AppContext.Provider value={context}>{children}</AppContext.Provider>;
};

export const useAppContext = () => useContext(AppContext);

type RENDER_MODES = 'desktop' | 'mobile' | 'native' | 'responsive' | 'beta';

AppContextContainer.defaultProps = {
  children: null,
  locale: undefined,
};
